import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Risk {
  id: string
  title: string
  category: string
  likelihood: string
  impact: string
  score: number
  status: string
  owner: string
  mitigation_progress: number
}

interface RiskCategory {
  category: string
  low: number
  medium: number
  high: number
  critical: number
  total_exposure: number
}

interface Statistics {
  total_risks: number
  critical_risks: number
  high_risks: number
  risks_mitigated: number
  total_exposure: number
  avg_mitigation_progress: number
}

export default function RiskManagementPage() {
  const [risks, setRisks] = useState<Risk[]>([])
  const [matrix, setMatrix] = useState<RiskCategory[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [risksRes, matrixRes, statsRes] = await Promise.all([
        fetch('/api/riskmanagement/risks'),
        fetch('/api/riskmanagement/matrix'),
        fetch('/api/riskmanagement/statistics')
      ])
      if (risksRes.ok) setRisks(await risksRes.json())
      if (matrixRes.ok) setMatrix(await matrixRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching risk management data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getImpactBadge = (impact: string) => {
    if (impact === 'critical') return 'bg-red-100 text-red-800'
    if (impact === 'high') return 'bg-orange-100 text-orange-800'
    if (impact === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'mitigating') return 'bg-blue-100 text-blue-800'
    if (status === 'monitoring') return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Risk Management</h1>
          <p className="mt-2 text-gray-600">Phase 254 - Enterprise risk assessment and mitigation</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Risks</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_risks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-2xl font-bold text-red-600">{statistics.critical_risks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">High</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.high_risks}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Mitigated</p>
              <p className="text-2xl font-bold text-green-600">{statistics.risks_mitigated}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Exposure</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.total_exposure)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Mitigation</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_mitigation_progress}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Risk Register</h2>
          </div>
          <div className="p-6 space-y-4">
            {risks.map((risk) => (
              <div key={risk.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{risk.title}</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getImpactBadge(risk.impact)}`}>{risk.impact}</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadge(risk.status)}`}>{risk.status}</span>
                  </div>
                  <span className="text-lg font-bold text-red-600">Score: {risk.score}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-3">
                  <div className="h-2 bg-blue-500 rounded-full" style={{width: `${risk.mitigation_progress}%`}}></div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Category</p>
                    <p className="font-medium">{risk.category}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Likelihood</p>
                    <p className="font-medium text-yellow-600">{risk.likelihood}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Owner</p>
                    <p className="font-medium text-blue-600">{risk.owner}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Progress</p>
                    <p className="font-medium text-green-600">{risk.mitigation_progress}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Risk Matrix by Category</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Low</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medium</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">High</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Critical</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exposure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {matrix.map((cat, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 font-medium text-gray-900">{cat.category}</td>
                    <td className="px-6 py-4 text-green-600">{cat.low}</td>
                    <td className="px-6 py-4 text-yellow-600">{cat.medium}</td>
                    <td className="px-6 py-4 text-orange-600">{cat.high}</td>
                    <td className="px-6 py-4 text-red-600 font-bold">{cat.critical}</td>
                    <td className="px-6 py-4 text-purple-600 font-medium">{formatCurrency(cat.total_exposure)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
